import React from 'react';

/**
 * Feb 2026 — Fixed bottom action bar for mobile forms.
 *
 * Sticks to the bottom of the viewport on phones/tablets, hosts a
 * big-thumb primary CTA and an optional secondary (Cancel /
 * Discard). Renders NOTHING on ≥md so desktop forms keep their
 * inline Save buttons.
 *
 * Consumers must add `pb-24` (or use the `<div className="h-24"/>`
 * spacer) inside their form so the last row doesn't sit under the
 * fixed bar.
 *
 * Props
 * -----
 *   primaryLabel   (str)   – e.g., "Save changes"
 *   onPrimary      (fn)    – primary tap handler
 *   primaryTestId  (str)
 *   primaryDisabled(bool)
 *   primaryLoading (bool)  – renders spinner + disables the button
 *   secondaryLabel (str)   – e.g., "Cancel". Hidden when omitted.
 *   onSecondary    (fn)
 *   secondaryTestId(str)
 *   danger         (bool)  – renders the primary in red (Delete confirms)
 */
const MobileFixedActions = ({
  primaryLabel = 'Save',
  onPrimary,
  primaryTestId,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  secondaryTestId,
  danger = false,
}) => {
  return (
    <div
      className="
        md:hidden fixed bottom-0 left-0 right-0 z-40
        bg-white/95 backdrop-blur border-t border-neutral-200
        px-4 py-3
        flex items-center gap-3
        shadow-[0_-6px_20px_-8px_rgba(15,23,42,0.15)]
        pb-[max(env(safe-area-inset-bottom,0),12px)]
      "
      data-testid="mobile-fixed-actions"
    >
      {secondaryLabel && (
        <button
          type="button"
          onClick={onSecondary}
          data-testid={secondaryTestId || 'mobile-fixed-actions-secondary'}
          className="
            flex-1 h-12 rounded-full
            border border-neutral-300
            text-[#0F172A] text-sm font-bold
            active:bg-neutral-100
          "
        >
          {secondaryLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onPrimary}
        disabled={primaryDisabled || primaryLoading}
        data-testid={primaryTestId || 'mobile-fixed-actions-primary'}
        className={`
          ${secondaryLabel ? 'flex-1' : 'w-full'} h-12 rounded-full
          text-white text-sm font-bold
          transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed
          ${danger
            ? 'bg-red-600 active:bg-red-700 hover:bg-red-700'
            : 'bg-[#0078D4] active:bg-[#005A9E] hover:bg-[#0069BF]'}
        `}
      >
        {primaryLoading ? (
          <span className="inline-flex items-center gap-2 justify-center">
            <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            <span>Working…</span>
          </span>
        ) : (
          primaryLabel
        )}
      </button>
    </div>
  );
};

export default MobileFixedActions;
